import { atom, selectorFamily } from 'recoil';

import { recoilPersist } from 'recoil-persist';

import { isOwnedByBugo } from './giftInputOwner';
import { ThanksSenderSuffix } from '../utils/thanksPrintMessage';

const { persistAtom } = recoilPersist({
  key: 'giftThanksPrint',
  storage: typeof window !== 'undefined' ? localStorage : undefined,
});

/**
 * 감사인쇄 입력값.
 * 감사글/발신자(이름·표현)는 인쇄 내용이라 상품과 무관하게 공통 → 주문하지 않아도 계속 보존하여
 * 다른 상품(감사인쇄 가능 브랜드) 주문 시에도 이어서 사용한다.
 * brandId/templateId(=디자인 타입)는 브랜드별로 다르므로, 같은 브랜드일 때만 복원/주문에 사용한다.
 *
 * 단, 보존 범위는 **같은 부고(상주)** 안으로 제한한다 — ownerBugoId 참고.
 */
export interface GiftThanksPrintState {
  /** 인쇄용 감사글 */
  message: string;
  /** 발신자 이름 (상주명, 없으면 OOO) */
  senderName: string;
  /** 발신 표현 (배상/드림/올림) */
  senderSuffix: ThanksSenderSuffix;
  /** 마지막으로 선택한 브랜드 id (템플릿 복원/주문 매칭용) */
  brandId?: string;
  /** 마지막으로 선택한 ThanksPrintTemplate._id */
  templateId?: string;
  /** 이 입력값을 작성한 부고(bugo)._id — 다른 상주 주문서에 새어나가지 않게 하는 도장 */
  ownerBugoId?: string;
}

export const giftThanksPrintAtom = atom<GiftThanksPrintState | null>({
  key: 'giftThanksPrintAtom',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

/**
 * 지금 보고 있는 부고의 감사인쇄 입력값 (명의가 다르면 null).
 * 주문서·요약·DTO는 항상 이 selector로 읽어야 한다 — atom을 직접 읽으면
 * 앞서 응대한 다른 상주의 감사글이 그대로 표시/전송된다.
 */
export const giftThanksPrintForBugoSelector = selectorFamily<
  GiftThanksPrintState | null,
  string | null
>({
  key: 'giftThanksPrintForBugoSelector',
  get:
    (bugoId) =>
    ({ get }) => {
      const selection = get(giftThanksPrintAtom);
      return selection && isOwnedByBugo(selection.ownerBugoId, bugoId)
        ? selection
        : null;
    },
});
